/**
 * Shadow DOM CSS utilities for embed style transformation and injection.
 *
 * Tamagui generates CSS targeting `:root` and `body`, which only apply in the
 * light DOM. When rendering embeds inside a Shadow DOM for CSS isolation, we
 * need to transform those selectors to their shadow DOM equivalents (`:host`)
 * and intercept runtime style injection so it lands inside the shadow root.
 */

/**
 * Transforms CSS selectors from document-scoped (`:root`, `body`) to shadow
 * DOM-scoped (`:host`) equivalents.
 *
 * Transformation rules:
 * - `:root { ... }` -> `:host { ... }`
 * - `:root .foo { ... }` -> `:host .foo { ... }`
 * - `:root.t_light { ... }` -> `:host(.t_light) { ... }`
 * - `:root.t_dark .t_light { ... }` -> `:host(.t_dark) .t_light { ... }`
 * - `body { ... }` -> `:host { ... }`
 */
export function transformCSSForShadowDOM(css: string): string {
  return (
    css
      // :root.t_light or :root.t_dark (class directly on :root) -> :host(.t_light) or :host(.t_dark)
      .replace(/:root\.([a-zA-Z_-]+)/g, ':host(.$1)')
      // :root followed by space, {, or , -> :host
      .replace(/:root(?=[\s{,])/g, ':host')
      // Standalone body selector -> :host
      .replace(/(?:^|\n)\s*body\s*\{/g, '\n:host {')
  )
}

/**
 * Injects a CSS string into a shadow root as a `<style>` element.
 *
 * Uses `<style>` elements rather than `adoptedStyleSheets` to ensure correct
 * CSS cascade order — adopted stylesheets have higher cascade priority than
 * `<style>` elements (per CSS spec), which can cause unintended overrides
 * when Tamagui's static CSS (e.g. `.is_Text .is_Text { display: inline-flex }`)
 * overrides atomic classes (e.g. `._dsp-inline { display: inline }`).
 */
export function injectCSSIntoShadowRoot(
  shadowRoot: ShadowRoot,
  css: string
): void {
  const style = document.createElement('style')
  style.textContent = css
  shadowRoot.appendChild(style)
}

/**
 * Transforms CSS for portaled content (tooltips, popovers) that Tamagui renders
 * outside the Shadow DOM into document.body.
 *
 * Since portaled elements live in the light DOM, they can't use `:host` selectors.
 * Instead, we transform `:root.t_light` → `.t_light` (class selector) so that
 * portal containers with the `t_light` class receive the theme's CSS variables.
 *
 * Transformation rules:
 * - `:root.t_light { ... }` → `.t_light { ... }` (class selector for portal spans)
 * - `:root { ... }` → `:root { ... }` (keep base variables on document root)
 * - `body { ... }` → removed (don't leak body styles to host page)
 */
export function transformCSSForPortals(css: string): string {
  return (
    css
      // :root.t_light or :root.t_dark (class directly on :root) -> .t_light or .t_dark
      .replace(/:root\.([a-zA-Z_-]+)/g, '.$1')
      // Remove body {} rules (don't leak to host page)
      .replace(/(?:^|\n)\s*body\s*\{[^}]*\}/g, '')
  )
}

/**
 * Injects CSS variable definitions into document.head so that portaled content
 * (Tamagui Tooltip, Popover, Sheet) rendered outside the Shadow DOM via
 * `createPortal(…, document.body)` can still access design tokens.
 *
 * The portal spans that Tamagui creates carry theme classes like `t_light`,
 * so `.t_light { --grey0: #FFF; … }` makes tokens available to them.
 *
 * Uses a unique ID to avoid duplicate injection across multiple SDK embeds.
 */
export function injectPortalCSS(css: string): () => void {
  const PORTAL_STYLE_ID = '_guild-sdk-portal-styles'

  // Only inject once even if multiple embeds are on the page
  if (document.head.querySelector(`#${PORTAL_STYLE_ID}`)) {
    return () => {}
  }

  const style = document.createElement('style')
  style.id = PORTAL_STYLE_ID
  style.textContent = transformCSSForPortals(css)
  document.head.appendChild(style)

  return () => {
    style.remove()
  }
}

/**
 * Known style element IDs that the SDK creates and should always be mirrored.
 * These are checked on initial scan of document.head (pre-existing elements).
 */
const KNOWN_STYLE_IDS = ['_tamagui-styles', 'react-native-stylesheet']

/**
 * Intercepts style injection into document.head so that CSS rules from
 * Tamagui, React Native Web, and webpack chunk CSS also apply inside the
 * shadow DOM.
 *
 * The SDK's dependencies inject CSS into document.head in several ways:
 *
 * 1. **Tamagui runtime** creates `<style id="_tamagui-styles">` and uses
 *    `sheet.insertRule()` for dynamic atomic CSS rules.
 *
 * 2. **React Native Web** creates `<style id="react-native-stylesheet">`
 *    with base layout rules (display:flex, flexDirection:column, etc.).
 *
 * 3. **Webpack MiniCSSExtractPlugin** creates `<link rel="stylesheet">`
 *    elements for chunk CSS files that contain Tamagui's compile-time
 *    extracted component CSS (e.g. `._pt-t-space-4 { padding-top: ... }`).
 *
 * This function:
 * - Scans existing `<style>` elements with known IDs and mirrors them.
 * - Watches for ALL new `<style>` and `<link>` elements added to document.head.
 * - Copies rules into matching `<style>` elements inside the shadow root.
 * - Proxies `insertRule()` on mirrored stylesheets for future dynamic rules.
 *
 * Returns a cleanup function that disconnects the observer.
 */
export function interceptHeadStyles(
  shadowRoot: ShadowRoot
): () => void {
  const mirroredElements = new WeakSet<Element>()

  /**
   * Copies all CSS rules from a source stylesheet into a shadow `<style>`
   * element, transforming :root/:body selectors to :host equivalents.
   * Also proxies `insertRule` so future dynamic rules are mirrored.
   */
  function mirrorStyleElement(styleEl: HTMLStyleElement): void {
    if (mirroredElements.has(styleEl)) return
    mirroredElements.add(styleEl)

    const shadowStyle = document.createElement('style')
    shadowStyle.dataset.mirrorOf = styleEl.id || 'anonymous'
    shadowRoot.appendChild(shadowStyle)

    const originalSheet = styleEl.sheet
    if (!originalSheet) return

    const shadowSheet = shadowStyle.sheet
    if (!shadowSheet) return

    // Copy all existing rules, transforming :root selectors to :host
    for (let i = 0; i < originalSheet.cssRules.length; i++) {
      try {
        shadowSheet.insertRule(
          transformCSSForShadowDOM(originalSheet.cssRules[i].cssText),
          shadowSheet.cssRules.length
        )
      } catch {
        // Some rules may be invalid in certain contexts; skip them
      }
    }

    // Proxy insertRule so future rules are also added to the shadow sheet
    const originalInsertRule = originalSheet.insertRule.bind(originalSheet)
    originalSheet.insertRule = (rule: string, index?: number): number => {
      const result = originalInsertRule(rule, index)
      try {
        shadowSheet.insertRule(
          transformCSSForShadowDOM(rule),
          shadowSheet.cssRules.length
        )
      } catch {
        // Some rules may be invalid in certain contexts; skip them
      }
      return result
    }
  }

  /**
   * Mirrors a `<link>` stylesheet into the shadow root.
   *
   * First attempts to read `sheet.cssRules` directly (same-origin). If that
   * fails due to CORS (cross-origin stylesheets), falls back to fetching
   * the CSS content via `fetch()` and injecting it as transformed text.
   */
  function mirrorLinkElement(linkEl: HTMLLinkElement): void {
    if (mirroredElements.has(linkEl)) return
    mirroredElements.add(linkEl)

    const injectCSS = (css: string) => {
      injectCSSIntoShadowRoot(shadowRoot, transformCSSForShadowDOM(css))
    }

    const tryReadSheet = (): boolean => {
      try {
        const sheet = linkEl.sheet
        if (!sheet) return false
        const rules: string[] = []
        for (let i = 0; i < sheet.cssRules.length; i++) {
          rules.push(sheet.cssRules[i].cssText)
        }
        if (rules.length > 0) {
          injectCSS(rules.join('\n'))
          return true
        }
      } catch {
        // CORS prevents reading cross-origin stylesheets
      }
      return false
    }

    const fetchAndInject = () => {
      if (!linkEl.href) return
      fetch(linkEl.href)
        .then((res) => {
          if (!res.ok) return
          return res.text()
        })
        .then((css) => {
          if (css) injectCSS(css)
        })
        .catch(() => {
          // Both approaches failed; skip this stylesheet
        })
    }

    const process = () => {
      if (!tryReadSheet()) {
        fetchAndInject()
      }
    }

    if (linkEl.sheet) {
      process()
    } else {
      linkEl.addEventListener('load', process, { once: true })
    }
  }

  // Scan pre-existing <style> elements with known IDs (Tamagui, RNW)
  for (const id of KNOWN_STYLE_IDS) {
    const existing = document.head.querySelector<HTMLStyleElement>(
      `style#${id}`
    )
    if (existing) {
      mirrorStyleElement(existing)
    }
  }

  // Watch document.head for new <style> and <link> elements.
  // Only newly-added elements are mirrored (not pre-existing host page styles).
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (let i = 0; i < mutation.addedNodes.length; i++) {
        const node = mutation.addedNodes[i]
        if (node instanceof HTMLStyleElement) {
          mirrorStyleElement(node)
        } else if (
          node instanceof HTMLLinkElement &&
          node.rel === 'stylesheet'
        ) {
          mirrorLinkElement(node)
        }
      }
    }
  })

  observer.observe(document.head, { childList: true })

  return () => {
    observer.disconnect()
  }
}
